import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { propEq } from 'ramda'
import useRole from '@/hooks/use-role'
import { colors, breakpoints, fontWeights } from '@/styles'
import { Label } from '@/components/text'
import Tooltip from '@/components/popper-tooltip'
import ActionButton from './action-button'
import DropdownMenu from '../tenancy-documents/dropdown-menu'
import RecipientsMenu from '../tenancy-documents/recipients-menu'
import NativeActionButton from '../tenancy-documents/native-action-button'
import FilesIcon from '../files-icon.svg'

import DownChevronIcon from '../down-chevron-icon.svg'

const isDraft = propEq('state_machine', 'draft')

const TableWrapper = styled.div`
  display: block;
  ${props => (props.header ? 'margin-bottom: 12px;' : 'margin-bottom: 38px;')}
  ${props => (props.primary ? 'margin-bottom: 38px;' : '')}
  ${props => (props.secondary ? 'margin-bottom: 12px;' : '')}
`

const TableTitle = styled.h5`
  font-size: 16px;
  font-weight: 500;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-bottom: 12px;
  }
`

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr 1fr 1fr 100px;
  gap: 16px;
  padding: 16px;
  align-items: center;
`

const TableLabelWrapper = styled(TableGrid)`
  color: ${colors.grey56};
`

const TableContentWapper = styled(TableGrid)`
  color: ${colors.black};
  background: ${colors.white};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
`

const TooltipOuter = styled.div`
  width: 250px;
`

const MobileTableWrapper = styled.div`
  background: ${colors.white};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`

const MobileTableHeaderContentWapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px;
  font-size: 14px;
`

const MobileTableBodyContentWrapper = styled.div`
  display: block;
  padding: 16px;
`

const MobileTableBodyContentLine = styled.div`
  width: 100%;
  border-top: 1px solid #dddddd;
  margin-bottom: 20px;
`

const MobileTableBodyContentInner = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 16px;
  color: ${colors.grey56};

  p {
    margin: 0;
    color: ${colors.black};
  }
`

const MobileTableContentColumns = styled.div`
  display: flex;
`
const MobileTableContentColumnsFileLabel = styled.p`
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: ${colors.grey56};
  margin-top: 0;
  margin-bottom: 0;
`

const MobileTableContentColumnsFileName = styled.p`
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: ${colors.black};
  margin-top: 0;
  margin-bottom: 0;
`

const MobileTableContentFileIcon = styled.img`
  margin-right: 12px;
`

const MobileTableContentDropdownIcon = styled.div`
  margin-right: 12px;
`

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;

  div {
    display: flex;
    align-items: center;
  }
`

const TableContentMoreButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`

const ChevronMenu = styled.img`
  display: inline-block;
  cursor: pointer;
  ${props => (props.reverse ? 'transform: rotate(180deg);' : '')}
`

const FullText = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 16px;
`

const TooltipWrapper = styled.div`
  p {
    max-width: 220px;
    margin: 0;
    padding: 0;
  }
`

const ContractName = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 16px;
  padding-right: 15px;
  width: fit-content;
  max-width: 250px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MobileTable = ({ documentStatus, contract, packageFile, viewContract, packageFiles }) => {
  const { t } = useTranslation()
  const role = useRole()

  const [contentVisible, setContentVisible] = useState(false)
  const isNativeContract = contract?.contract_structure === 'native' ? true : false

  return (
    <MobileTableWrapper>
      <MobileTableHeaderContentWapper>
        <MobileTableContentColumns>
          <MobileTableContentFileIcon src={FilesIcon} alt="icon" />
          <div>
            <MobileTableContentColumnsFileLabel>
              {t('l.contract_manager.documents.file_name.label', 'File Name')}
            </MobileTableContentColumnsFileLabel>
            <MobileTableContentColumnsFileName>
              {isNativeContract
                ? t(
                    'l.contract_manager.documents.provincial_tenancy_agreement.title',
                    'Provincial Tenancy Agreement'
                  )
                : packageFile?.file_original_name}
            </MobileTableContentColumnsFileName>
          </div>
        </MobileTableContentColumns>
        <MobileTableContentColumns>
          <MobileTableContentDropdownIcon>
            <RecipientsMenu
              viewContract={viewContract}
              isNativeContract={isNativeContract}
              packageFile={packageFile}
              contract={contract}
              packageFiles={packageFiles}
            />
            <DropdownMenu
              viewContract={viewContract}
              isNativeContract={isNativeContract}
              contractId={contract?.id}
              isMobile={true}
              role={role}
              contract={contract}
              packageFile={packageFile}
            />
          </MobileTableContentDropdownIcon>
          <div>
            <ChevronMenu
              src={DownChevronIcon}
              alt="icon"
              onClick={() => setContentVisible(!contentVisible)}
              reverse={contentVisible}
            />
          </div>
        </MobileTableContentColumns>
      </MobileTableHeaderContentWapper>
      {contentVisible && (
        <MobileTableBodyContentWrapper>
          <MobileTableBodyContentLine />
          <MobileTableBodyContentInner>
            <div>{t('l.contract_manager.documents.type.label', 'Type')}</div>
            <div>
              <p>
                {isNativeContract
                  ? t('l.contract_manager.documents.regular_contract.title', 'Regular Contract')
                  : t('l.contract_manager.documents.liv_contract.title', 'Liv Contract')}
              </p>
            </div>
          </MobileTableBodyContentInner>
          <MobileTableBodyContentInner>
            <div>{t('l.contract_manager.documents.status.label', 'Status')}</div>
            <div>{documentStatus}</div>
          </MobileTableBodyContentInner>
        </MobileTableBodyContentWrapper>
      )}
    </MobileTableWrapper>
  )
}

const Tables = ({
  title,
  documentStatus,
  signatureStatus,
  header,
  primary,
  secondary,
  isMobile,
  contract,
  packageFile,
  order,
  signers,
  packageFiles
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  const role = useRole()
  const isNativeContract = contract?.contract_structure === 'native' ? true : false

  const viewContract = () => {
    const getNewUrl = (role, contract) => {
      const where = role === 'tenant' ? 'rental' : 'listing'

      if (isDraft(contract))
        return `/listing/${contract?.listing_id}/contract/create/${
          contract?.listing_contract_id || ''
        }`
      return `/${where}/${contract?.listing_id}/contract/${contract?.id}`
    }

    history.push(getNewUrl(role, contract))
  }

  return (
    <TableWrapper header={header} primary={primary} secondary={secondary}>
      {header === true && (
        <>
          <TableTitle>{title}</TableTitle>

          {!isMobile && (
            <TableLabelWrapper>
              <Label fontSize="14px" type="small">
                {t('l.contract_manager.documents.file.label', 'File')}
              </Label>
              <Label fontSize="14px" type="small">
                {t('l.contract_manager.documents.status.label', 'Status')}
              </Label>
              <Label fontSize="14px" type="small">
                {t('l.contract_manager.documents.type.label', 'Type')}
              </Label>
            </TableLabelWrapper>
          )}
        </>
      )}

      {!isMobile ? (
        <TableContentWapper>
          <TooltipOuter>
            <Tooltip
              theme="secondary"
              enabled
              size="medium"
              content={
                <TooltipWrapper>
                  <FullText>{packageFile.file_original_name}</FullText>
                </TooltipWrapper>
              }
              placement="top"
            >
              <ContractName>
                {isNativeContract
                  ? t(
                      'l.contract_manager.documents.provincial_tenancy_agreement.title',
                      'Provincial Tenancy Agreement'
                    )
                  : packageFile.file_original_name}
              </ContractName>
            </Tooltip>
          </TooltipOuter>

          <StatusWrapper>{documentStatus}</StatusWrapper>
          <Label fontSize="14px" type="small">
            {isNativeContract
              ? t('l.contract_manager.documents.regular_contract.title', 'Regular Contract')
              : t('l.contract_manager.documents.liv_contract.title', 'Liv Contract')}
          </Label>

          {isNativeContract ? (
            <NativeActionButton
              contract={contract}
              packageFile={packageFile}
              order={order}
              signers={signers}
            />
          ) : (
            <ActionButton
              contract={contract}
              packageFile={packageFile}
              order={order}
              signers={signers}
            />
          )}
          <TableContentMoreButtonWrapper>
            <RecipientsMenu
              viewContract={viewContract}
              isNativeContract={isNativeContract}
              packageFile={packageFile}
              contract={contract}
              packageFiles={packageFiles}
            />
            <DropdownMenu
              viewContract={viewContract}
              isNativeContract={isNativeContract}
              contractId={contract?.id}
              role={role}
              contract={contract}
              packageFile={packageFile}
            />
          </TableContentMoreButtonWrapper>
        </TableContentWapper>
      ) : (
        <MobileTable
          documentStatus={documentStatus}
          signatureStatus={signatureStatus}
          contract={contract}
          packageFile={packageFile}
          viewContract={viewContract}
          packageFiles={packageFiles}
        />
      )}
    </TableWrapper>
  )
}

export default Tables
